import React, { useCallback, useEffect, useState } from "react";
import "./PublicationsDetection.scss";
import { ReactComponent as AggregationTypeAndIcon } from "src/images/aggregation_type_and.svg";
import { ReactComponent as AggregationTypeOrIcon } from "src/images/aggregation_type_or.svg";
import { ReactComponent as CheckIcon } from "src/images/selected.svg";

import axios from "axios";
import { uuidv7 as uuid } from "uuidv7";
import { useDispatch, useSelector } from "react-redux";

import { differenceInCalendarDays } from "date-fns";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useIntl } from "react-intl";
import { translateMessage } from "src/app/methods/translateMessage";
import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { SettingsButtons } from "src/app/SettingsModal/components/SettingsTabContent/SettingsTabContent";
import { Status } from "src/app/components/Status/Status";
import { getProject } from "src/redux";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "../../../../../../utils/API_URLS";
import { showToast } from "../../../../../methods/showToast";
import {
  selectOptionsAreEqual,
  showErrorToast,
} from "../../../../../../utils/methods";
import { refreshAutomationList } from "../../../../../../redux/automation/automationActions";

import { ModalText, ModalTitle } from "../../../../../components/Modal/Modal";
import { Button } from "../../../../../components/Button/Button";
import { CustomDateRange } from "../../../../../components/CustomDateRange/CustomDateRange";
import { CustomInputLabel } from "../../../../../components/CustomInput/CustomInput";

import { ReactComponent as LighteningIcon } from "../../../../../../images/lightening.svg";
import { ReactComponent as PowerIcon } from "../../../../../../images/power.svg";
import { ReactComponent as SaveIcon } from "../../../../../../images/floppy-disk.svg";
import { CustomTokenInput } from "../../../../../components/CustomTokenInput/CustomTokenInput";
import {
  AutomationAggregationTypes,
  AutomationsSection,
} from "../../Automations";
import AutomationsEnum from "../../AutomationsEnum";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "../../../../../components/Dropdown/Dropdown";
import moment from "moment";
import Tooltip from "src/app/components/Tooltip/Tooltip";

interface Props {
  setSection: (section: AutomationsSection) => void;
  basicData?: any;
}

export default function PublicationsDetection({
  setSection,
  basicData,
}: Props) {
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [selectedDaysCount, setSelectedDaysCount] = useState<number | null>(
    null,
  );
  const [frequency, setFrequency] = useState<number | null>(null);
  const [aggregationType, setAggregationType] = useState<string | null>(
    AutomationAggregationTypes.OrType,
  );
  const [keywords, setKeywords] = useState<string[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [hashtags, setHashtags] = useState<any[]>([]);

  // loading
  const [createLoading, setCreateLoading] = useState(false);
  const [enableLoading, setEnableLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const { projectId } = basicData;

  const {
    automationReducer: { editedAutomationData },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();
  const intl = useIntl();

  const goBack = () => setSection(AutomationsSection.AutomationsMain);

  useEffect(() => {
    if (
      !editedAutomationData?.settings ||
      editedAutomationData?.wsProjectUuid !== projectId
    )
      return;

    // Date From
    if (editedAutomationData.settings?.dateFrom) {
      setDateFrom(editedAutomationData.settings?.dateFrom);
    }
    // Date To
    if (editedAutomationData.settings?.dateTo) {
      setDateTo(editedAutomationData.settings?.dateTo);
    }
    //Frequency
    if (editedAutomationData.settings?.frequency) {
      setFrequency(editedAutomationData.settings?.frequency);
    }
    // Aggregation Type
    if (editedAutomationData.settings?.aggregationType) {
      setAggregationType(editedAutomationData.settings?.aggregationType);
    }
    // Keywords
    if (editedAutomationData.settings?.keywords) {
      const newKeywords = editedAutomationData.settings?.keywords || [];

      setKeywords(newKeywords);
    }
    // Hashtags
    if (editedAutomationData.settings?.hashtags) {
      const newHashtags = editedAutomationData.settings?.hashtags || [];

      setHashtags(newHashtags);
    }
    // Tags
    if (editedAutomationData.settings?.mentions) {
      const newTags = editedAutomationData.settings?.mentions || [];

      setTags(newTags);
    }
  }, [editedAutomationData, projectId]);

  const clearFrequency = useCallback(() => {
    setSelectedDaysCount(null);
    setFrequency(null);
  }, []);

  const calculateRefreshInterval = useCallback(() => {
    if (!dateFrom || !dateTo) {
      clearFrequency();
      return;
    }

    const INTERVAL_RULES = [
      { maxDays: 7, intervalHours: 3 },
      { maxDays: 30, intervalHours: 6 },
      { maxDays: 90, intervalHours: 12 },
    ];

    const formattedDateFrom = new Date(dateFrom);
    const formattedDateTo = new Date(dateTo);

    const days =
      differenceInCalendarDays(formattedDateTo, formattedDateFrom) + 1;

    const rule = INTERVAL_RULES.find((r) => days <= r.maxDays);

    if (!rule) {
      clearFrequency();
      return;
    }

    setSelectedDaysCount(days);
    setFrequency(rule.intervalHours);
  }, [dateFrom, dateTo, clearFrequency]);

  useEffect(() => {
    calculateRefreshInterval();
  }, [calculateRefreshInterval]);

  const reloadData = () => {
    dispatch(refreshAutomationList(projectId || basicData.projectId));
    dispatch(getProject(projectId || basicData.projectId));
  };

  const handleErrors = (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.data?.errors) {
      const key = error.response?.data?.errors[0];
      showErrorToast({
        id: key,
        defaultMessage: key,
      });
    } else {
      showErrorToast();
    }
  };

  const runAutomation = async ({
    uuid,
    wsProjectUuid,
    wsAutomationName,
    settings,
  }: {
    uuid: string;
    wsProjectUuid: string;
    wsAutomationName: string;
    settings: any;
  }) => {
    const url = API_URLS.runAutomation;

    try {
      await axios.post(url, {
        uuid,
        wsProjectUuid,
        wsAutomationName,
        settings,
      });
    } catch (error) {
      handleErrors(error);
    }
  };

  const saveAutomationChanges = async () => {
    setSaveLoading(true);
    const url = API_URLS.updateAutomation;

    try {
      await axios.put(url, {
        uuid: editedAutomationData.uuid,
        wsProjectUuid: projectId,
        wsAutomationName: AutomationsEnum.PublicationDetectionRunnableAutomation,
        settings: {
          dateTo,
          dateFrom,
          frequency,
          aggregationType,
          keywords,
        },
      });
      reloadData();
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_changes_saved_successfully"
          defaultMessage="Changes saved successfully."
        />,
      );
    } catch (error) {
      handleErrors(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const toggleAutomation = async () => {
    setEnableLoading(true);
    const newValue = !editedAutomationData.enabled;

    try {
      const toggleUrl = API_URLS.toggleAutomation;

      const payload: any = {
        uuid: editedAutomationData.uuid,
        enabled: newValue,
      };

      if (differencesDetected) {
        payload.settings = {
          dateTo,
          dateFrom,
          frequency,
          aggregationType,
          keywords,
        };
        payload.wsAutomationName = AutomationsEnum.PublicationDetectionRunnableAutomation;
      }

      await axios.post(toggleUrl, payload);

      if (newValue) {
        showToast(
          "info",
          <IDHFormattedMessage
            id="ws_automation_enabled"
            defaultMessage="Automation enabled"
          />,
          <IDHFormattedMessage
            id="ws_we_will_inform_you_about_detected_publications"
            defaultMessage="We will inform you about detected publications."
          />,
        );
        runAutomation({
          uuid: editedAutomationData.uuid,
          wsProjectUuid: projectId || basicData.projectId,
          wsAutomationName: AutomationsEnum.PublicationDetectionRunnableAutomation,
          settings: {
            dateTo,
            dateFrom,
            aggregationType,
            keywords,
            frequency,
          },
        });
      } else {
        showToast(
          "success",
          <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
          <IDHFormattedMessage
            id="ws_automation_disabled"
            defaultMessage="Automation disabled."
          />,
        );
      }
      goBack();
      reloadData();
    } catch (error) {
      handleErrors(error);
    } finally {
      setEnableLoading(false);
    }
  };

  const createAutomation = async () => {
    setCreateLoading(true);
    const url = API_URLS.createAutomation;
    const automationUuid = uuid();
    const newAutomationParams = {
      uuid: automationUuid,
      wsProjectUuid: projectId || basicData.projectId,
      wsAutomationName: AutomationsEnum.PublicationDetectionRunnableAutomation,
      settings: {
        dateTo,
        dateFrom,
        frequency,
        aggregationType,
        keywords,
      },
    };

    try {
      await axios.post(url, newAutomationParams);
      runAutomation(newAutomationParams);
      setCreateLoading(false);
      reloadData();
      goBack();
    } catch (error) {
      handleErrors(error);
    }
  };

  const formatDate = (date: Date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const renderDaysText = (days: number | null) => {
    if (!days) return null;
    return days === 1 ? (
      <IDHFormattedMessage id="ws_day" defaultMessage="day" />
    ) : (
      <IDHFormattedMessage id="ws_days" defaultMessage="days" />
    );
  };

  const renderStatusMessage = () => {
    if (isExpired) {
      return (
        <>
          <IDHFormattedMessage
            id="ws_detecting_ended"
            defaultMessage="Detecting ended"
          />
          &nbsp;
          {expirationDiff === 0 ? (
            <IDHFormattedMessage id="ws_today" defaultMessage="today" />
          ) : (
            <>
              &nbsp;{expirationDiff}&nbsp;
              {renderDaysText(expirationDiff)}
              &nbsp;
              <IDHFormattedMessage id="ws_ago" defaultMessage="ago" />
            </>
          )}
        </>
      );
    }

    if (editedAutomationData?.enabled) {
      if (hasStarted) {
        return (
          <>
            <IDHFormattedMessage
              id="ws_detecting_ends"
              defaultMessage="Detecting ends"
            />
            &nbsp;
            {expirationDiff === 0 ? (
              <IDHFormattedMessage id="ws_today" defaultMessage="today" />
            ) : (
              <>
                <IDHFormattedMessage id="ws_in" defaultMessage="in" />
                &nbsp;{expirationDiff}&nbsp;
                {renderDaysText(expirationDiff)}
              </>
            )}
          </>
        );
      }
      return (
        <>
          <IDHFormattedMessage
            id="ws_detecting_starts_in"
            defaultMessage="Detecting starts in"
          />
          &nbsp;{startingDiff}&nbsp;
          {renderDaysText(startingDiff)}
        </>
      );
    }

    return (
      <IDHFormattedMessage
        id="ws_not_detecting_publications"
        defaultMessage="Not detecting publications"
      />
    );
  };

  const datesFromEqual = dateFrom === editedAutomationData.settings?.dateFrom;
  const datesToEqual = dateTo === editedAutomationData.settings?.dateTo;
  const keywordsEqual = selectOptionsAreEqual(
    keywords,
    editedAutomationData.settings?.keywords,
  );
  const hashtagsEqual = selectOptionsAreEqual(
    hashtags,
    editedAutomationData.settings?.hashtags,
  );
  const tagsEqual = selectOptionsAreEqual(
    tags,
    editedAutomationData.settings?.mentions,
  );

  const differencesDetected =
    editedAutomationData.uuid &&
    (!datesFromEqual ||
      !datesToEqual ||
      !hashtagsEqual ||
      !tagsEqual ||
      !keywordsEqual);

  const dayDifference =
    dateFrom && dateTo
      ? differenceInCalendarDays(new Date(dateTo), new Date(dateFrom)) || 31
      : 31;

  const today = new Date();
  const formattedToday = formatDate(today);

  const automationDateFrom = editedAutomationData.settings?.dateFrom;
  const automationDateTo = editedAutomationData.settings?.dateTo;
  const isExpired = Boolean(
    automationDateTo && automationDateTo < formattedToday,
  );
  const isLocalExpired = Boolean(dateTo && dateTo < formattedToday);
  const tooBigRange = Boolean(dateFrom && dateTo && dayDifference > 60);
  const noDate = Boolean(!dateFrom || !dateTo);
  const blockDate = isLocalExpired || tooBigRange || noDate;
  const hasStarted = Boolean(
    automationDateTo && automationDateTo >= formattedToday,
  );
  const startingDiff = automationDateFrom
    ? differenceInCalendarDays(new Date(automationDateFrom), today)
    : null;
  const expirationDiff = automationDateTo
    ? Math.abs(differenceInCalendarDays(new Date(automationDateTo), today))
    : null;

  const hideTooltip =
    !editedAutomationData?.enabled && !hasStarted && !isExpired;

  return (
    <>
      <AnimatedDiv className="publications-detection">
        <span className="publications-detection__link" onClick={goBack}>
          <IDHFormattedMessage
            id="ws_automations"
            defaultMessage="Automations"
          />
        </span>
        <ModalTitle>
          <IDHFormattedMessage
            id="ws_publications_detection"
            defaultMessage="Publications detection"
          />
          <Status
            className={
              isExpired
                ? "status--red-01"
                : editedAutomationData?.enabled
                  ? "status--green-01"
                  : "status--grey-01"
            }
          >
            {isExpired ? (
              <IDHFormattedMessage id="ws_expired" defaultMessage="Expired" />
            ) : editedAutomationData?.enabled ? (
              <IDHFormattedMessage id="ws_active" defaultMessage="Active" />
            ) : (
              <IDHFormattedMessage id="ws_disabled" defaultMessage="Disabled" />
            )}
          </Status>

          <Tooltip
            content={hasStarted || isExpired ? automationDateTo : automationDateFrom}
            contentHidden={hideTooltip}
            center
            small
          >
            <span className="publications-detection__status-text">
              {renderStatusMessage()}
            </span>
          </Tooltip>
        </ModalTitle>
        <ModalText>
          <IDHFormattedMessage
            id="ws_publications_detection_modal_text"
            defaultMessage="Please set the timeframe for the automatic post detection to be active. Please note that all provided hashtags and mentions must be included in the publication caption for it to be detected."
          />
        </ModalText>

        {/* Duration */}
        <div className="publications-detection__row">
          <CustomInputLabel htmlFor="duration">
            <IDHFormattedMessage id="ws_duration" defaultMessage="Duration" />
          </CustomInputLabel>
          <CustomDateRange
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            showError={isLocalExpired || tooBigRange}
            minDate={new Date()}
            errorMessage={
              isLocalExpired ? (
                <IDHFormattedMessage
                  id="ws_date_is_expired"
                  defaultMessage="Date is expired"
                />
              ) : (
                <IDHFormattedMessage
                  id="ws_date_must_be_within_60_days"
                  defaultMessage="Date range must be within 60 days"
                />
              )
            }
          />

          <div className="publications-detection__frequency">
            {dateFrom && dateTo && !tooBigRange ? (
              <>
                {selectedDaysCount}&nbsp;
                <IDHFormattedMessage id="ws_days" defaultMessage="Days" />・
                <IDHFormattedMessage
                  id="ws_detectionEvery"
                  defaultMessage="Detection every"
                />
                &nbsp;{frequency}&nbsp;
                {frequency === 1 ? (
                  <IDHFormattedMessage id="ws_hour" defaultMessage="hour" />
                ) : (
                  <IDHFormattedMessage id="ws_hours" defaultMessage="hours" />
                )}
              </>
            ) : (
              <IDHFormattedMessage
                id="ws_youll_find_out_the_duration_of_the_detection_and_its_frequency"
                defaultMessage="You'll find out the duration of the detection and its frequency"
              />
            )}
          </div>
        </div>

        {/* Keywords */}
        <div className="publications-detection__row">
          <CustomInputLabel htmlFor="tags">
            <IDHFormattedMessage id="ws_keywords" defaultMessage="Keywords" />
          </CustomInputLabel>
          <div className="publications-detection__keywords-row">
            <CustomTokenInput
              value={keywords}
              onChange={(newValue: string[]) => setKeywords(newValue)}
              placeholder={translateMessage({
                intl,
                id: "ws_enter_keywords_to_detect",
                defaultMessage: "Enter keywords to detect",
              })}
              separators={[","]}
            />
            <Dropdown toRight>
              {(isOpenAggregationType, setIsOpenAggregationType) => (
                <>
                  <Button
                    variant="light-grey"
                    size="large"
                    onClick={() => setIsOpenAggregationType((prev) => !prev)}
                    className="action-dropdown__button"
                  >
                    {aggregationType === AutomationAggregationTypes.OrType ? (
                      <AggregationTypeOrIcon />
                    ) : (
                      <AggregationTypeAndIcon />
                    )}
                  </Button>
                  <DropdownMenu
                    isOpen={isOpenAggregationType}
                    onClick={() => setIsOpenAggregationType(false)}
                  >
                    <DropdownMenuItem
                      className="publications-detection__aggregation-row"
                      onClick={() =>
                        setAggregationType(AutomationAggregationTypes.OrType)
                      }
                    >
                      <AggregationTypeOrIcon className="publications-detection__aggregation-icon" />
                      <div className="publications-detection__aggregation-text">
                        <div className="publications-detection__aggregation-text-title">
                          <IDHFormattedMessage
                            id="ws_publication_detection_or"
                            defaultMessage="At least one"
                          />
                          {aggregationType ===
                            AutomationAggregationTypes.OrType && (
                            <CheckIcon className="publications-detection__aggregation-check-icon" />
                          )}
                        </div>
                        <div className="publications-detection__aggregation-text-description">
                          <IDHFormattedMessage
                            id="ws_publication_detection_or_description"
                            defaultMessage="The publication must contain at least one entered keyword."
                          />
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="publications-detection__aggregation-row"
                      onClick={() =>
                        setAggregationType(AutomationAggregationTypes.AndType)
                      }
                    >
                      <AggregationTypeAndIcon className="publications-detection__aggregation-icon" />
                      <div className="publications-detection__aggregation-text">
                        <div className="publications-detection__aggregation-text-title">
                          <IDHFormattedMessage
                            id="ws_publication_detection_and"
                            defaultMessage="All selected"
                          />
                          {aggregationType ===
                            AutomationAggregationTypes.AndType && (
                            <CheckIcon className="publications-detection__aggregation-check-icon" />
                          )}
                        </div>
                        <div className="publications-detection__aggregation-text-description">
                          <IDHFormattedMessage
                            id="ws_publication_detection_and_description"
                            defaultMessage="The publication must contain all entered keywords."
                          />
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenu>
                </>
              )}
            </Dropdown>
          </div>
        </div>
      </AnimatedDiv>
      <SettingsButtons>
        <div className="publications-detection__buttons">
          <div className="publications-detection__buttons-wrapper">
            <Button
              className="publications-detection__buttons-back"
              size="large"
              variant="white-with-border"
              onClick={goBack}
            >
              <IDHFormattedMessage id="ws_back" defaultMessage="Back" />
            </Button>
          </div>
          <div className="publications-detection__buttons-wrapper publications-detection__buttons-wrapper--multiple">
            {differencesDetected ? (
              <Button
                size="large"
                variant="white-with-border"
                onClick={saveAutomationChanges}
                disabled={saveLoading || keywords.length === 0 || blockDate}
              >
                <SaveIcon />
                <IDHFormattedMessage id="ws_save" defaultMessage="Save" />
              </Button>
            ) : null}

            {editedAutomationData.enabled === true ? (
              <Button
                size="large"
                variant="blue"
                onClick={toggleAutomation}
                disabled={enableLoading || keywords.length === 0 || blockDate}
              >
                <PowerIcon />
                <IDHFormattedMessage id="ws_disable" defaultMessage="Disable" />
              </Button>
            ) : null}

            {editedAutomationData.enabled === false ? (
              <Button
                size="large"
                variant="blue"
                onClick={toggleAutomation}
                disabled={
                  enableLoading ||
                  isExpired ||
                  keywords.length === 0 ||
                  blockDate
                }
              >
                <LighteningIcon />
                <IDHFormattedMessage
                  id="ws_enable_run_now"
                  defaultMessage="Enable & run now"
                />
              </Button>
            ) : null}

            {!editedAutomationData.uuid ? (
              <Button
                size="large"
                variant="blue"
                onClick={createAutomation}
                disabled={!keywords.length || blockDate || createLoading}
              >
                <LighteningIcon />
                <IDHFormattedMessage
                  id="ws_enable_run_now"
                  defaultMessage="Enable & run now"
                />
              </Button>
            ) : null}
          </div>
        </div>
      </SettingsButtons>
    </>
  );
}
